import { GridRowSelectionModel } from "@mui/x-data-grid";
import { useMemo } from "react";
import {
  ActionStatus,
  ActionType,
  IActionResponseItem,
  PolicyState,
  actionConfig,
} from "shared";
import { Button } from "@mui/material";
import { When } from "react-if";

import { useActions, useActionTableModels } from "../../hooks/useActions";
import {
  useApproveActionsMutation,
  useDeleteSkippedActionsMutation,
  useRegenerateChildActionsBulkMutation,
  useRevertApprovalsMutation,
  useSkipActionsMutation,
} from "../../services/api/actionApi/action";
import { useGetSelectedOrganisation } from "../../hooks/useGetSelectedOrganisation";

import { ActionQuickFilters } from "./ActionQuickFilters";

interface BulkUpdatesToolbarProps {
  rowSelectionModel: GridRowSelectionModel;
}

export function BulkUpdatesToolbar({
  rowSelectionModel,
}: BulkUpdatesToolbarProps) {
  const { data: actionsRes } = useActions();
  const [approveActions, { isLoading: approveActionsLoading }] = useApproveActionsMutation();
  const [deleteSkippedActions, { isLoading: deleteSkippedActionsLoading }] = useDeleteSkippedActionsMutation();
  const [revertApprovals, { isLoading: revertApprovalsLoading }] = useRevertApprovalsMutation();
  const [regenerateBulk, { isLoading: regenerateBulkLoading }] = useRegenerateChildActionsBulkMutation();
  const [skipActions, { isLoading: skipActionsLoading }] = useSkipActionsMutation();
  const currentOrg = useGetSelectedOrganisation();
  const { currentTab } = useActionTableModels();

  const actionsDetail: {
    action: IActionResponseItem;
    canApprove: boolean;
    canSkip: boolean;
    canDeleteSkipped: boolean;
    canRevertApproval: boolean;
    canRegenerateChildActions: boolean;
  }[] = useMemo(() => {
    return [...rowSelectionModel.ids]
      .map(id => {
        const matchedAction = actionsRes?.actions.find(action => action.id === id);

        if (!matchedAction) return null;

        return {
          action: matchedAction,
          canApprove: matchedAction.status === ActionStatus.APPROVAL_PENDING,
          canSkip: [ActionStatus.APPROVAL_PENDING, ActionStatus.PENDING].includes(matchedAction.status)
            && actionConfig[matchedAction.type]?.canBeSkipped === true,
          canDeleteSkipped: matchedAction.status === ActionStatus.SKIPPED,
          canRevertApproval: matchedAction.status === ActionStatus.PENDING && matchedAction.policies.some(policy => policy.state === PolicyState.APPROVAL),
          canRegenerateChildActions: matchedAction.status === ActionStatus.COMPLETE && matchedAction.type === ActionType.LATE_FEE,
        };
      })
      .filter(action => action);
  }, [actionsRes?.actions, rowSelectionModel.ids]);

  const hasLateFees = actionsRes?.actions.some(action => action.type === ActionType.LATE_FEE && action.status === ActionStatus.COMPLETE);

  const totalCanRevertApproval = actionsDetail.filter(action => action.canRevertApproval).length;
  const totalCanApprove = actionsDetail.filter(action => action.canApprove).length;
  const totalCanSkip = actionsDetail.filter(action => action.canSkip).length;
  const totalCanDeleteSkipped = actionsDetail.filter(action => action.canDeleteSkipped).length;
  const totalCanRegenerateChildActions = actionsDetail.filter(action => action.canRegenerateChildActions).length;

  function onApprove() {
    approveActions({
      organisationId: currentOrg.id,
      actionIds: actionsDetail.filter(action => action.canApprove).map(action => action.action.id),
      force: false,
    });
  }

  function onApproveAndForceRun() {
    approveActions({
      organisationId: currentOrg.id,
      actionIds: actionsDetail.filter(action => action.canApprove).map(action => action.action.id),
      force: true,
    });
  }

  function onRevertApproval() {
    revertApprovals({
      organisationId: currentOrg.id,
      actionIds: actionsDetail.filter(action => action.canRevertApproval).map(action => action.action.id),
    });
  }

  function onSkip() {
    skipActions({
      organisationId: currentOrg.id,
      actionIds: actionsDetail.filter(action => action.canSkip).map(action => action.action.id),
    });
  }

  function onDeleteSkipped() {
    deleteSkippedActions({
      organisationId: currentOrg.id,
      actionIds: actionsDetail.filter(action => action.canDeleteSkipped).map(action => action.action.id),
    });
  }

  function onRegenerateBulk() {
    regenerateBulk({
      organisationId: currentOrg.id,
      actionIds: actionsDetail.filter(action => action.canRegenerateChildActions).map(action => action.action.id),
    });
  }

  return (
    <div className={ `flex items-center` }>
      <ActionQuickFilters />

      { /* Display on draft, pending, or all tabs */ }
      <When condition={ !currentTab || currentTab === ActionStatus.APPROVAL_PENDING || currentTab === ActionStatus.PENDING }>
        <Button
          disabled={ totalCanSkip === 0 || skipActionsLoading }
          onClick={ onSkip }
          size={ `small` }
        >
          {
            skipActionsLoading ?
              `Skipping...` :
              `Skip ${ totalCanSkip } action${totalCanSkip === 1 ? `` : `s`}`
          }
        </Button>
      </When>

      <When condition={ currentTab === ActionStatus.SKIPPED }>
        <Button
          disabled={ totalCanDeleteSkipped === 0 || deleteSkippedActionsLoading }
          onClick={ onDeleteSkipped }
          size={ `small` }
          color={ `error` }
        >
          {
            deleteSkippedActionsLoading ?
              `Deleting...` :
              `Delete ${ totalCanDeleteSkipped } action${totalCanDeleteSkipped === 1 ? `` : `s`}`
          }
        </Button>
      </When>

      <When condition={ !currentTab || currentTab === ActionStatus.APPROVAL_PENDING }>
        <Button
          disabled={ totalCanApprove === 0 || approveActionsLoading }
          onClick={ onApprove }
          size={ `small` }
        >
          {
            approveActionsLoading ?
              `Approving...` :
              `Approve ${ totalCanApprove } action${totalCanApprove === 1 ? `` : `s`}`
          }
        </Button>
      </When>

      <When condition={ !currentTab || currentTab === ActionStatus.APPROVAL_PENDING }>
        <Button
          disabled={ totalCanApprove === 0 || approveActionsLoading }
          onClick={ onApproveAndForceRun }
          size={ `small` }
        >
          {
            approveActionsLoading ?
              `Approving to run now...` :
              `Approve ${ totalCanApprove } action${totalCanApprove === 1 ? `` : `s`} and Run Now `
          }
        </Button>
      </When>

      { /* Only display on the all, or approved tab */ }
      <When condition={ !currentTab || currentTab === ActionStatus.PENDING }>
        <Button
          disabled={ totalCanRevertApproval === 0 || revertApprovalsLoading }
          onClick={ onRevertApproval }
          size={ `small` }
        >
          {
            revertApprovalsLoading ?
              `Reverting Approval...` :
              `Undo Approval ${ totalCanRevertApproval } action${totalCanRevertApproval === 1 ? `` : `s`}`
          }
        </Button>
      </When>

      { /* Only Display on the all or complete tab */ }
      <When condition={ (!currentTab || currentTab === ActionStatus.COMPLETE) && hasLateFees }>
        <Button
          disabled={ totalCanRegenerateChildActions === 0 || regenerateBulkLoading }
          onClick={ onRegenerateBulk }
          size={ `small` }
        >
          {
            regenerateBulkLoading ?
              `Regenerating...` :
              `Regenerate emails for ${ totalCanRegenerateChildActions } action${totalCanRegenerateChildActions === 1 ? `` : `s`}`
          }
        </Button>
      </When>
    </div>
  );
}
